import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-context";
import { MobileAppListViewModel } from "./mobile-app-list.view-model";
import EntityList from "../entity-list/entity-list";
import { IBrandingAppModel, IMobileAppModel } from "src/types/models/apiModels";
import { ReactComponent as IPhoneIcon } from "../../../assets/svg/apple.svg";
import { ReactComponent as AndroidIcon } from "../../../assets/svg/android.svg";
import { ReactComponent as CopyIcon } from "../../../assets/svg/copyIcon.svg";
import { BusinessViewModel } from "../business-list/business-list.view-model";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { ClipboardService } from "src/helpers/clipboard-service";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import ButtonWithIcon from "src/components/atoms/button-with-icon/button-with-icon";
import CopyToClipboardButton from "src/components/atoms/copy-to-clipboard-button/copy-to-clipboard-button";

interface MobileAppListProps {
	selectMode?: boolean;
	onAddNewClick?: () => void;
	onSelectClicked?: (mobileApp: IBrandingAppModel) => void;
	onViewClicked?: (mobileApp: IBrandingAppModel) => void;
	onDeleteClicked?: (mobileApp: IMobileAppModel) => void;
	onEditClicked?: (mobileApp: IMobileAppModel) => void;
}

const MobileAppList: React.FC<MobileAppListProps> = ({
	selectMode,
	onAddNewClick,
	onViewClicked,
	onEditClicked,
	onSelectClicked,
	onDeleteClicked,
}) => {
	const { user } = useAuth(); // Extract user from useAuth hook
	const [data, setData] = useState<IMobileAppModel[]>([]);
	const [loading, setLoading] = useState(true);
	const [canCreateMobileApp, setCanCreateMobileApp] = useState(false);
	const viewModel = useRef<MobileAppListViewModel | null>(null);
	const businessListViewModel = useRef<BusinessViewModel | null>(null);

	useEffect(() => {
		const canAddNewBrandingApplication = async () => {
			let hasApprovedBusiness = false;

			if (user && user.accessToken) {
				businessListViewModel.current = new BusinessViewModel(
					user.accessToken
				);

				const businesses =
					await businessListViewModel.current.fetchListAsync();

				hasApprovedBusiness =
					businesses.filter(
						(p) => p.status === "Active" || p.status === "Approved"
					).length > 0;
			}

			const userPermissions = user?.permissionBag.permissions;

			if (
				hasApprovedBusiness &&
				userPermissions &&
				userPermissions.filter(
					(p) => p.name === "BrandingApp" && p.value === "Create"
				).length > 0
			) {
				setCanCreateMobileApp(true);
			} else {
				setCanCreateMobileApp(false);
			}
		};

		canAddNewBrandingApplication();
	}, []);

	useEffect(() => {
		fetchData();
	}, [user]);

	const fetchData = async () => {
		setLoading((prevValue) => {
			if (!prevValue) {
				return true;
			}
			return prevValue;
		});
		try {
			if (user && user.accessToken) {
				viewModel.current = new MobileAppListViewModel(
					user.accessToken
				);
				const response = await viewModel.current?.fetchList();
				setData(() => response);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setLoading((prevValue) => {
				if (prevValue) {
					return false;
				}
				return prevValue;
			});
		}
	};

	const handleSelectMobileApp = (mobileApp: IMobileAppModel) => {
		if (onSelectClicked) {
			onSelectClicked({
				option: "ExternalApp",
				brandingAppCode: mobileApp.code,
				brandingAppId: mobileApp.id,
				brandingAppName: mobileApp.name,
			});
		}
	};

	const handleEditMobileApp = (mobileApp: IMobileAppModel) => {
		if (onEditClicked) {
			onEditClicked(mobileApp);
		}
	};

	const handleUseSecuredCallsApp = () => {
		if (onSelectClicked) {
			onSelectClicked({
				option: "SecuredCallsApp",
				brandingAppName: "Secured Call App",
				brandingAppCode: "SC-BRANDING-APP",
			});
		}
	};

	const handleDeleteMobileApp = async (mobileApp: IMobileAppModel) => {
		if (user && user.accessToken && viewModel.current) {
			setLoading(true);
			const mobileAppDeleted = await viewModel.current.deleteAsync(
				mobileApp.id
			);
			setLoading(false);
			if (mobileAppDeleted) {
				await fetchData();
			}
		}
	};

	const renderMobileAppItem = (app: IMobileAppModel) => (
		<div className="flex flex-col items-start justify-center gap-2 w-full p-2">
			<div className="flex items-center justify-center gap-2">
				<span className="text-lg font-semibold">{app.name}</span>
			</div>
			<div className="flex items-center gap-2">
				{/* iOS App Section */}
				<div className="flex items-center gap-1 bg-gradient-to-r from-sky-300 to-sky-400 rounded drop-shadow-lg">
					<div className="border-r-2 border-gray-600 p-2">
						<IPhoneIcon className="w-6 h-6"></IPhoneIcon>
					</div>
					<span className="font-Poppins font-medium text-md p-2 text-blue-900 tracking-wider">
						{app.iOSAppSettings?.bundleId || "No Bundle ID"}
					</span>
					{app?.iOSAppSettings?.bundleId && (
						<CopyToClipboardButton
							text={app.iOSAppSettings.bundleId}
						></CopyToClipboardButton>
					)}
				</div>

				{/* Android App Section */}
				<div className="flex items-center gap-1 bg-gradient-to-r from-sky-300 to-sky-400 rounded drop-shadow-lg">
					<div className="border-r-2 border-gray-600 p-2">
						<AndroidIcon className="w-6 h-6"></AndroidIcon>
					</div>
					<span className="font-Poppins font-medium text-md p-2 text-blue-900 tracking-wider">
						{app.androidAppSettings?.packageName ||
							"No Package Name"}
					</span>
					{app?.androidAppSettings?.packageName && (
						<CopyToClipboardButton
							text={app.androidAppSettings.packageName}
						></CopyToClipboardButton>
					)}
				</div>
			</div>
		</div>
	);

	if (loading) {
		return (
			<LoadingModal
				message="Loading your mobile apps data, please hold on!"
				open={loading}
			/>
		); // Pass the optional message
	}

	return (
		<div className="flex flex-col items-start gap-4">
			{selectMode && (
				<>
					<div className="flex items-center justify-center gap-4">
						<ButtonPro
							onClick={handleUseSecuredCallsApp}
							label={"Use Secured Calls mobile app"}
						></ButtonPro>
						<ButtonWithIcon
							onClick={function (): void {
								const iOSAppUrl = `https://apps.apple.com/au/app/secured-calls/id6502035232`;
								ClipboardService.copyToClipboard(iOSAppUrl);
							}}
							icon={
								<>
									<IPhoneIcon className="w-6 h-6"></IPhoneIcon>
									<CopyIcon className="w-6 h-6"></CopyIcon>
								</>
							}
							className="bg-gradient-to-r from-sky-400 to-sky-600 p-2 shadow-[5px_5px_10px_rgba(0,0,0,.5)]"
						></ButtonWithIcon>
						<ButtonWithIcon
							onClick={function (): void {
								const androidAppUrl = `https://play.google.com/store/apps/details?id=com.es.sc&pcampaignid=web_share`;
								ClipboardService.copyToClipboard(androidAppUrl);
							}}
							icon={
								<>
									<AndroidIcon className="w-6 h-6"></AndroidIcon>
									<CopyIcon className="w-6 h-6"></CopyIcon>
								</>
							}
							className="bg-gradient-to-r from-sky-400 to-sky-600 p-2 shadow-[5px_5px_10px_rgba(0,0,0,.5)]"
						></ButtonWithIcon>
					</div>
					{data.length > 0 && (
						<span className="font-Poppins font-semibold text-md tracking-wider">
							OR Select from your own apps below
						</span>
					)}
				</>
			)}
			<EntityList<IMobileAppModel>
				data={data}
				selectMode={selectMode} // or false depending on your requirements
				onItemSelected={(app) => handleSelectMobileApp(app)}
				onEditClicked={(app) => handleEditMobileApp(app)}
				onViewClicked={(app) => handleEditMobileApp(app)}
				onDeleteClicked={async (app) => {
					await handleDeleteMobileApp(app);
				}}
				renderChildren={renderMobileAppItem}
				onAddNewClick={onAddNewClick}
				canAddNew={canCreateMobileApp}
				helpText={[]}
				listLabel={"Choose Your Mobile app"}
				filterText="Filter by mobile app name"
				enableView={false}
				enableDelete={true}
			/>
		</div>
	);
};

export default MobileAppList;
